import { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../utils/api';
import { Layout } from '../components/Layout';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Eye, Warning, Truck, PencilSimple, Trash, Package } from '@phosphor-icons/react';
import { useAuth } from '../context/AuthContext';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import { formatDate } from '../utils/dateFormat';
import { SearchFilterBar, EmptySearchResult, LoadErrorState } from '../components/SearchFilterBar';
import { CapacityCalendar } from '../components/CapacityCalendar';

const STATUS_MAP = {
  pending_procurement: { label: 'Pending Procurement', className: 'bg-amber-50 text-amber-700 border-amber-200' },
  procurement_approved: { label: 'Procurement Approved', className: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  procurement_rejected: { label: 'Rejected', className: 'bg-red-50 text-red-700 border-red-200' },
  assigned_to_production: { label: 'Assigned to Production', className: 'bg-blue-50 text-blue-700 border-blue-200' },
  under_production: { label: 'Under Production', className: 'bg-purple-50 text-purple-700 border-purple-200' },
  production_on_hold: { label: 'Production On Hold', className: 'bg-amber-50 text-amber-700 border-amber-200' },
  production_completed: { label: 'Production Completed', className: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  scheduled_for_dispatch: { label: 'Scheduled for Dispatch', className: 'bg-cyan-50 text-cyan-700 border-cyan-200' },
  ready_to_dispatch: { label: 'Ready to Dispatch', className: 'bg-orange-50 text-orange-700 border-orange-200' },
  partially_dispatched: { label: 'Partially Dispatched', className: 'bg-orange-50 text-orange-700 border-orange-200' },
  dispatched: { label: 'Dispatched', className: 'bg-zinc-50 text-zinc-700 border-zinc-200' },
  pending_for_installation: { label: 'Pending Installation', className: 'bg-indigo-50 text-indigo-700 border-indigo-200' },
  installation_completed: { label: 'Installation Completed', className: 'bg-teal-50 text-teal-700 border-teal-200' }
};

function StatusBadge({ status }) {
  const config = STATUS_MAP[status] || { label: status, className: 'bg-zinc-50 text-zinc-700' };
  return <Badge className={`${config.className} border`}>{config.label}</Badge>;
}

const ADMIN_SEARCH_FIELDS = [
  { key: 'jo_number', label: 'JO Number', placeholder: 'Enter JO number' },
  { key: 'customer_name', label: 'Customer Name', placeholder: 'Enter customer name' },
  { key: 'customer_mobile', label: 'Customer Mobile', placeholder: 'Enter mobile number' },
  {
    key: 'executive_name', label: 'Executive Name', type: 'select',
    options: [{ value: 'all', label: 'All Executives' }],
    dynamic: true
  }
];

const EXEC_SEARCH_FIELDS = [
  { key: 'jo_number', label: 'JO Number', placeholder: 'Enter JO number' },
  { key: 'customer_mobile', label: 'Customer Mobile', placeholder: 'Enter mobile number' },
  { key: 'customer_name', label: 'Customer Name', placeholder: 'Enter customer name' },
  {
    key: 'status', label: 'Status', type: 'select',
    options: [
      { value: 'all', label: 'All Statuses' },
      ...Object.entries(STATUS_MAP).map(([key, { label }]) => ({ value: key, label }))
    ]
  }
];

const PRIORITY_MAP = { 'High': 10, 'Medium': 5, 'Low': 1 };

function JobOrderCard({ jo, user, isSelected, onToggleSelect, onView, onEdit, onDelete }) {
  const isExecutive = user?.role === 'executive';
  const isAdmin = user?.role === 'admin';
  const EXEC_EDITABLE = ['pending_procurement', 'procurement_rejected'];
  const isEditable = isExecutive && jo.created_by === user?.user_id && EXEC_EDITABLE.includes(jo.status);
  const isRejected = jo.status === 'procurement_rejected';
  const isAssignable = isAdmin && jo.status === 'procurement_approved';

  const borderClass = isRejected || jo.has_urgent_request ? 'border-l-4 border-l-red-500' : '';

  return (
    <Card data-testid={`job-order-${jo.jo_number}`} className={`border-zinc-300 rounded-sm shadow-sm ${borderClass}`}>
      <CardContent className="p-6">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          {isAssignable && (
            <div className="flex items-center mr-2">
              <Checkbox data-testid={`select-jo-${jo.jo_number}`} checked={isSelected} onCheckedChange={onToggleSelect} />
            </div>
          )}
          {isExecutive && jo.status === 'production_completed' && (
            <div className="flex items-center mr-2">
              <Checkbox data-testid={`select-jo-${jo.jo_number}`} checked={isSelected} onCheckedChange={onToggleSelect} />
            </div>
          )}
          <div className="space-y-3 flex-1">
            <div className="flex items-center gap-3 flex-wrap">
              <h3 className="text-xl font-bold text-zinc-950">JO #{jo.jo_number}</h3>
              <StatusBadge status={jo.status} />
              {(() => {
                const src = (jo.source || jo.origin || '').toLowerCase();
                const isMobile = src === 'mobile';
                return (
                  <Badge
                    data-testid={`jo-origin-badge-${jo.jo_number}`}
                    className={isMobile
                      ? 'bg-blue-100 text-blue-700 hover:bg-blue-100 border border-blue-200'
                      : 'bg-zinc-100 text-zinc-700 hover:bg-zinc-100 border border-zinc-300'}
                  >
                    {isMobile ? 'Mobile JO' : 'Manual JO'}
                  </Badge>
                );
              })()}
              {jo.has_urgent_request && (
                <Badge className="bg-red-100 text-red-800 border border-red-300">
                  <Warning size={14} weight="bold" className="mr-1" /> Urgent
                </Badge>
              )}
            </div>
            {isRejected && jo.procurement_remarks && (
              <div className="bg-red-50 border border-red-200 rounded-sm px-3 py-1.5">
                <span className="text-xs font-bold text-red-500">Rejection Reason: </span>
                <span className="text-xs text-red-700">{jo.procurement_remarks}</span>
              </div>
            )}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-sm">
              {[
                ['Customer', jo.customer_name],
                ['Mobile', jo.customer_mobile],
                ['Delivery Date', formatDate(jo.delivery_date) || jo.delivery_date],
                ['Windows', `${jo.no_of_windows} units`],
                ['Sqft', jo.total_sqft],
                ['Executive', jo.executive_name],
                ['Location', jo.work_location]
              ].map(([label, value]) => (
                <div key={label}>
                  <span className="text-zinc-500 font-bold uppercase text-xs">{label}:</span>
                  <p className="text-zinc-950">{value}</p>
                </div>
              ))}
            </div>
            {Array.isArray(jo.items_summary) && jo.items_summary.some(it => it.width && it.height) && (
              <div className="flex flex-wrap gap-1.5" data-testid={`jo-dims-${jo.jo_number}`}>
                <span className="text-zinc-500 font-bold uppercase text-xs mr-1 self-center">Dimensions:</span>
                {jo.items_summary.filter(it => it.width && it.height).slice(0, 10).map((it, i) => (
                  <span key={i} className="text-xs font-mono bg-zinc-100 border border-zinc-200 rounded px-1.5 py-0.5 text-zinc-700">
                    {it.width}×{it.height}{it.quantity > 1 ? ` ×${it.quantity}` : ''}
                  </span>
                ))}
                {jo.items_summary.filter(it => it.width && it.height).length > 10 && (
                  <span className="text-xs text-zinc-400 self-center">+more</span>
                )}
              </div>
            )}
          </div>
          <div className="flex gap-2 flex-shrink-0">
            <Button data-testid={`view-job-${jo.jo_number}`} onClick={onView} variant="outline" className="h-11">
              <Eye size={20} weight="bold" className="mr-2" /> View
            </Button>
            {isEditable && (
              <>
                <Button data-testid={`edit-job-${jo.jo_number}`} onClick={onEdit} variant="outline" className="h-11 border-blue-300 text-blue-700 hover:bg-blue-50">
                  <PencilSimple size={20} weight="bold" />
                </Button>
                <Button data-testid={`delete-job-${jo.jo_number}`} onClick={onDelete} variant="outline" className="h-11 border-red-300 text-red-600 hover:bg-red-50">
                  <Trash size={20} weight="bold" />
                </Button>
              </>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function DispatchRequestDialog({ open, onOpenChange, selectedJos, remarks, setRemarks, onSubmit, loading }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md bg-white">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Truck size={22} weight="bold" className="text-blue-600" /> Request Dispatch / Installation
          </DialogTitle>
          <DialogDescription className="sr-only">Request dispatch for selected job orders</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 pt-2">
          <div className="bg-zinc-50 p-3 rounded-sm">
            <p className="text-sm font-bold text-zinc-700">Selected JOs: {selectedJos.join(', ')}</p>
          </div>
          <div className="space-y-2">
            <label className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500">Remarks / Instructions</label>
            <Textarea data-testid="request-remarks" value={remarks} onChange={e => setRemarks(e.target.value)} placeholder="Add remarks or special instructions..." rows={3} />
          </div>
          <Button data-testid="submit-dispatch-request" onClick={onSubmit} disabled={loading} className="w-full bg-blue-600 hover:bg-blue-700 text-white font-bold h-11">
            {loading ? 'Sending...' : `Send Request (${selectedJos.length} JOs)`}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function TotalsBar({ count, totalWindows, totalSqft }) {
  return (
    <Card className="border-zinc-300 rounded-sm shadow-sm bg-zinc-50">
      <CardContent className="p-4">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <p className="text-sm font-bold uppercase tracking-[0.2em] text-zinc-500">Totals ({count} orders)</p>
          <div className="flex gap-8">
            <div className="text-center">
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500">Total Windows</p>
              <p data-testid="total-windows" className="text-2xl font-bold text-zinc-950">{totalWindows}</p>
            </div>
            <div className="text-center">
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500">Total Sqft</p>
              <p data-testid="total-sqft" className="text-2xl font-bold text-zinc-950">{totalSqft.toFixed(2)}</p>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

const JO_DEFAULT_FILTERS = {
  jo_number: '',
  customer_mobile: '',
  customer_name: '',
  status: 'all',
  executive_name: 'all'
};

export function JobOrders() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [jobOrders, setJobOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [selectedJos, setSelectedJos] = useState([]);
  const [requestDialog, setRequestDialog] = useState(false);
  const [requestRemarks, setRequestRemarks] = useState('');
  const [requestLoading, setRequestLoading] = useState(false);
  const [filters, setFilters] = useState(JO_DEFAULT_FILTERS);
  const [execNames, setExecNames] = useState([]);
  const [priority, setPriority] = useState('Medium');
  const [calendarRefresh, setCalendarRefresh] = useState(0);

  const isExecutive = user?.role === 'executive';
  const isAdmin = user?.role === 'admin';

  // Fetch executive names for admin filter
  useEffect(() => {
    if (isAdmin) {
      api.get('/api/reports/executive-names')
        .then(res => setExecNames(res.data || []))
        .catch(() => {});
    }
  }, [isAdmin]);

  // Build search fields with dynamic executive names
  const searchFields = useMemo(() => {
    if (isAdmin) {
      return ADMIN_SEARCH_FIELDS.map(f => {
        if (f.key === 'executive_name') {
          return { ...f, options: [{ value: 'all', label: 'All Executives' }, ...execNames.map(n => ({ value: n, label: n }))] };
        }
        return f;
      });
    }
    return EXEC_SEARCH_FIELDS;
  }, [isAdmin, execNames]);

  const fetchJobOrders = useCallback(async (searchFilters) => {
    setLoading(true);
    setLoadError(false);
    try {
      const params = new URLSearchParams();
      const f = searchFilters || JO_DEFAULT_FILTERS;

      // Admin defaults to pending_assignment list (procurement_approved only)
      if (isAdmin && (!f.status || f.status === 'all')) {
        params.append('list_type', 'pending_assignment');
      }

      Object.entries(f).forEach(([key, value]) => {
        if (value && value !== 'all') params.append(key, value);
      });
      const { data } = await api.get(`/api/job-orders?${params}`);
      setJobOrders(data);
    } catch (err) {
      if (err?.isAuthError) return;
      setLoadError(true);
      toast.error('Failed to load job orders. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [isAdmin]);

  useEffect(() => { fetchJobOrders(); }, [fetchJobOrders]);

  const handleSearch = () => { fetchJobOrders(filters); };
  const handleReset = (cleared) => { setFilters(cleared); fetchJobOrders(cleared); };
  const handleRefresh = () => { fetchJobOrders(filters); };
  const toggleSelect = (joNum) => { setSelectedJos(prev => prev.includes(joNum) ? prev.filter(j => j !== joNum) : [...prev, joNum]); };

  const handleRequestDispatch = async () => {
    if (selectedJos.length === 0) return;
    setRequestLoading(true);
    try {
      await api.post(`/api/installation-requests`, { jo_numbers: selectedJos, remarks: requestRemarks });
      toast.success(`Request sent for ${selectedJos.length} job orders`);
      setSelectedJos([]);
      setRequestRemarks('');
      setRequestDialog(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send request');
    } finally { setRequestLoading(false); }
  };

  const handleDeleteJo = async (joNumber) => {
    if (!window.confirm(`Are you sure you want to delete Job Order ${joNumber}?`)) return;
    try {
      if (joNumber.includes('/')) {
        await api.post('/api/job-orders/delete-by-body', { jo_number: joNumber });
      } else {
        await api.post(`/api/job-orders/${joNumber}/delete`, {});
      }
      toast.success(`Job Order ${joNumber} deleted successfully`);
      fetchJobOrders();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete job order');
    }
  };

  const handleBulkAssign = async () => {
    if (selectedJos.length === 0) { toast.error('Please select at least one job order'); return; }
    try {
      const { data } = await api.post('/api/job-orders/bulk-assign-production',
        { jo_numbers: selectedJos, priority: PRIORITY_MAP[priority] || 5 });
      const schedCount = data?.scheduled?.length || 0;
      toast.success(`${selectedJos.length} Job Orders assigned to production${schedCount ? ` — ${schedCount} auto-scheduled` : ''}`);
      setSelectedJos([]);
      fetchJobOrders(filters);
      setCalendarRefresh(k => k + 1);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to assign to production');
    }
  };

  const { totalWindows, totalSqft, completedJos } = useMemo(() => ({
    totalWindows: jobOrders.reduce((sum, jo) => sum + (jo.no_of_windows || 0), 0),
    totalSqft: jobOrders.reduce((sum, jo) => sum + (jo.total_sqft || 0), 0),
    completedJos: jobOrders.filter(jo => jo.status === 'production_completed')
  }), [jobOrders]);

  // Admin view: group JOs by planned production start (unscheduled first)
  const groupedJos = useMemo(() => {
    if (!isAdmin) return null;
    const groups = new Map();
    jobOrders.forEach(jo => {
      const key = jo.planned_production_start ? String(jo.planned_production_start).slice(0, 10) : 'unscheduled';
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key).push(jo);
    });
    const keys = [...groups.keys()].sort((a, b) => {
      if (a === 'unscheduled') return -1;
      if (b === 'unscheduled') return 1;
      return a.localeCompare(b);
    });
    return keys.map(key => ({ key, jos: groups.get(key) }));
  }, [isAdmin, jobOrders]);

  return (
    <Layout>
      <div className="space-y-6 pb-20">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div>
            <h1 className="text-4xl font-light tracking-tight text-zinc-950">
              {isAdmin ? 'Assign to Production' : 'Job Orders'}
            </h1>
            <p className="text-sm text-zinc-500 mt-2">
              {isAdmin ? 'Procurement approved JOs pending production assignment' : 'Search and manage job orders'}
            </p>
          </div>
          <div className="flex items-center gap-3">
            {isAdmin && selectedJos.length > 0 && (
              <>
                <Select value={priority} onValueChange={setPriority}>
                  <SelectTrigger className="w-40 h-11"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="High">High Priority</SelectItem>
                    <SelectItem value="Medium">Medium Priority</SelectItem>
                    <SelectItem value="Low">Low Priority</SelectItem>
                  </SelectContent>
                </Select>
                <Button data-testid="bulk-assign-button" onClick={handleBulkAssign} className="bg-blue-600 hover:bg-blue-700 text-white font-bold h-11">
                  <Package size={20} weight="bold" className="mr-2" /> Assign {selectedJos.length} to Production
                </Button>
              </>
            )}
            <Button data-testid="new-job-order-button" onClick={() => navigate('/job-orders/new')} className="bg-blue-600 hover:bg-blue-700 text-white font-bold h-11">
              + New Job Order
            </Button>
          </div>
        </div>

        {isAdmin && <CapacityCalendar refreshKey={calendarRefresh} />}

        <SearchFilterBar
          fields={searchFields}
          filters={filters}
          setFilters={setFilters}
          onSearch={handleSearch}
          onReset={handleReset}
          onRefresh={handleRefresh}
          loading={loading}
          resultCount={jobOrders.length}
        />

        {isExecutive && completedJos.length > 0 && (
          <Card className="border-blue-300 rounded-sm shadow-sm bg-blue-50">
            <CardContent className="p-4 flex items-center justify-between flex-wrap gap-3">
              <div>
                <p className="text-sm font-bold text-blue-700">
                  {selectedJos.length > 0 ? `${selectedJos.length} production-completed JO(s) selected` : `${completedJos.length} production-completed JO(s) available for dispatch request`}
                </p>
                <p className="text-xs text-blue-500">Select JOs and request dispatch/installation</p>
              </div>
              <Button data-testid="request-dispatch-btn" onClick={() => setRequestDialog(true)} disabled={selectedJos.length === 0} className="bg-blue-600 hover:bg-blue-700 text-white font-bold h-10">
                <Truck size={16} className="mr-2" /> Request Dispatch ({selectedJos.length})
              </Button>
            </CardContent>
          </Card>
        )}

        {loading && <div className="text-center py-12">Loading job orders...</div>}
        {!loading && loadError && <LoadErrorState onRetry={handleRefresh} />}
        {!loading && !loadError && jobOrders.length === 0 && (
          <EmptySearchResult onReset={() => handleReset(JO_DEFAULT_FILTERS)} />
        )}
        {!loading && !loadError && jobOrders.length > 0 && (
          <>
            {isAdmin && groupedJos ? (
              <div className="space-y-6">
                {groupedJos.map(group => (
                  <div key={group.key} className="space-y-3" data-testid={`jo-group-${group.key}`}>
                    <div className="flex items-center gap-3 border-b border-zinc-200 pb-1.5">
                      <h2 className={`text-sm font-bold uppercase tracking-[0.15em] ${group.key === 'unscheduled' ? 'text-amber-600' : 'text-zinc-700'}`}>
                        {group.key === 'unscheduled' ? 'Unscheduled' : `Production Start: ${formatDate(group.key) || group.key}`}
                      </h2>
                      <span className="text-xs text-zinc-400">
                        {group.jos.length} JO{group.jos.length > 1 ? 's' : ''} · {group.jos.reduce((s, j) => s + (j.no_of_windows || 0), 0)} windows
                      </span>
                    </div>
                    <div className="space-y-4">
                      {group.jos.map(jo => (
                        <JobOrderCard
                          key={jo.jo_number}
                          jo={jo}
                          user={user}
                          isSelected={selectedJos.includes(jo.jo_number)}
                          onToggleSelect={() => toggleSelect(jo.jo_number)}
                          onView={() => navigate(`/job-orders/${jo.jo_number}`)}
                          onEdit={() => navigate(`/job-orders/${jo.jo_number}/edit`)}
                          onDelete={() => handleDeleteJo(jo.jo_number)}
                        />
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="space-y-4">
                {jobOrders.map(jo => (
                  <JobOrderCard
                    key={jo.jo_number}
                    jo={jo}
                    user={user}
                    isSelected={selectedJos.includes(jo.jo_number)}
                    onToggleSelect={() => toggleSelect(jo.jo_number)}
                    onView={() => navigate(`/job-orders/${jo.jo_number}`)}
                    onEdit={() => navigate(`/job-orders/${jo.jo_number}/edit`)}
                    onDelete={() => handleDeleteJo(jo.jo_number)}
                  />
                ))}
              </div>
            )}
            <TotalsBar count={jobOrders.length} totalWindows={totalWindows} totalSqft={totalSqft} />
          </>
        )}

        <DispatchRequestDialog
          open={requestDialog}
          onOpenChange={setRequestDialog}
          selectedJos={selectedJos}
          remarks={requestRemarks}
          setRemarks={setRequestRemarks}
          onSubmit={handleRequestDispatch}
          loading={requestLoading}
        />
      </div>
    </Layout>
  );
}
